import { useEffect, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import dayjs from "dayjs";
import { zonedTimeToUtc } from "date-fns-tz";
import { APP_TIMEZONE } from "@accredo/fe-shared/services/formatService.js";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import FormTextField from "../../components/Form/controls/FormTextField.jsx";
import FormSelect from "../../components/Form/controls/FormSelect.jsx";
import FormSwitch from "../../components/Form/controls/FormSwitch.jsx";
import FormDatePicker from "../../components/Form/controls/FormDatePicker.jsx";
import FormDnDFileUpload from "../../components/Form/FormDnDFileUpload.jsx";
import ConsentDocumentVersionsTable from "../../components/tables/ConsentDocumentVersionsTable.jsx";
import { useConsentDocumentActions } from "../../hooks/useConsentDocumentActions.jsx";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";

const DOCUMENT_TYPES = [
  { key: "privacy", label: "Datenschutz" },
  { key: "terms", label: "Nutzungsbedingungen" },
];

const emptyFormValues = {
  document_type: "privacy",
  version: "",
  title: "",
  description: "",
  pdf_file: null,
  effective_from: dayjs(),
  effective_until: null,
  is_active: false,
};

const toStartOfDayUtc = (value) => {
  if (!value) return null;
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  if (!d.isValid()) return null;
  const local = new Date(d.year(), d.month(), d.date(), 0, 0, 0);
  return zonedTimeToUtc(local, APP_TIMEZONE).toISOString();
};

const toEndOfDayUtc = (value) => {
  if (!value) return null;
  const d = dayjs.isDayjs(value) ? value : dayjs(value);
  if (!d.isValid()) return null;
  const local = new Date(d.year(), d.month(), d.date(), 23, 59, 59);
  return zonedTimeToUtc(local, APP_TIMEZONE).toISOString();
};

const ConsentDocumentAdministrationPage = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { versions, isLoading, createVersion, updateVersion } =
    useConsentDocumentActions();

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Einstellungen", path: "/admin/settings" },
      { name: "Einwilligungsdokumente", path: "/admin/consent-documents" },
    ]);
  }, [setBreadcrumbs]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const serverErrorRef = useRef(null);

  const formMethods = useForm({ defaultValues: emptyFormValues });
  const {
    handleSubmit,
    reset,
    watch,
    setError: setFieldError,
    formState: { isSubmitting, errors },
  } = formMethods;

  const setError = (field, error) => {
    setFieldError(field, error);
    setFieldError("root.serverError", { type: "manual" });
  };

  useEffect(() => {
    if (errors.root?.serverError) {
      serverErrorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [errors.root?.serverError]);

  const [versionValue, titleValue, effectiveFrom, pdfFile] = watch([
    "version",
    "title",
    "effective_from",
    "pdf_file",
  ]);

  const isSubmitDisabled =
    (!editingVersion && !versionValue) ||
    !titleValue ||
    !(editingVersion || pdfFile?.length) ||
    !effectiveFrom ||
    isSubmitting;

  const handleOpenDialog = (version = null) => {
    setEditingVersion(version);
    reset(
      version
        ? {
            document_type: version.document_type || "privacy",
            version: version.version || "",
            title: version.title || "",
            description: version.description || "",
            pdf_file: null,
            effective_from: version.effective_from
              ? dayjs(version.effective_from)
              : null,
            effective_until: version.effective_until
              ? dayjs(version.effective_until)
              : null,
            is_active: version.is_active ?? false,
          }
        : emptyFormValues
    );
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingVersion(null);
  };

  const handleFormSubmit = async (data) => {
    try {
      const { pdf_file, effective_from, effective_until, ...versionData } =
        data;
      versionData.effective_from = toStartOfDayUtc(effective_from);
      versionData.effective_until = toEndOfDayUtc(effective_until);

      let success;
      if (editingVersion) {
        success = await updateVersion(editingVersion.id, versionData, setError);
      } else {
        success = await createVersion(
          versionData,
          pdf_file[0].originalFile,
          setError
        );
      }
      if (success) {
        handleCloseDialog();
      }
    } catch {
      // error already handled by hook
    }
  };

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" gutterBottom>
          Einwilligungsdokumente
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Verwalten Sie Dokumente, die eine Benutzerzustimmung benötigen, wie
          Datenschutz- und Nutzungsbedingungsdokumente. Neue Versionen werden
          Benutzern automatisch zur Zustimmung vorgelegt.
        </Typography>
      </Box>

      <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Neue Dokumenten-Version erstellen
        </Button>
      </Box>

      <ConsentDocumentVersionsTable
        versions={versions}
        isLoading={isLoading}
        onEdit={handleOpenDialog}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingVersion
            ? "Dokumenten-Version bearbeiten"
            : "Neue Dokumenten-Version erstellen"}
        </DialogTitle>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <DialogContent>
              {errors.root?.serverError && (
                <Alert ref={serverErrorRef} severity="error" sx={{ mb: 2 }}>
                  Bitte korrigieren Sie die markierten Felder.
                </Alert>
              )}
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                <FormSelect
                  name="document_type"
                  label="Dokumenttyp"
                  options={DOCUMENT_TYPES}
                  disabled={!!editingVersion}
                />

                <FormTextField
                  name="version"
                  label="Versionsnummer"
                  placeholder="z.B. 1.0, 2.1, 2024.1"
                  fullWidth
                  required={!editingVersion}
                  disabled={!!editingVersion}
                />

                <FormTextField
                  name="title"
                  label="Titel"
                  placeholder="z.B. Datenschutzerklärung 2024"
                  fullWidth
                  required
                />

                <FormTextField
                  name="description"
                  label="Beschreibung"
                  multiline
                  rows={3}
                  fullWidth
                />

                <FormDnDFileUpload
                  name="pdf_file"
                  label="PDF-Dokument hochladen"
                  allowedExtensions={["pdf"]}
                />

                <FormDatePicker name="effective_from" label="Gültig ab" />

                <FormDatePicker
                  name="effective_until"
                  label="Gültig bis (optional)"
                  clearable
                />

                <FormSwitch
                  name="is_active"
                  label="Aktiv"
                  disabled={!!editingVersion && editingVersion.is_active}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <DsButton onClick={handleCloseDialog} variant="tertiary">
                Abbrechen
              </DsButton>
              <DsButton type="submit" disabled={isSubmitDisabled}>
                {editingVersion ? "Speichern" : "Erstellen"}
              </DsButton>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};

export default ConsentDocumentAdministrationPage;
